import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  ThemeProvider,
  Typography,
  AppBar,
  Tabs,
  Tab,
  List,
  ListItem as ProtoListItem,
  ListItemText,
  ListItemAvatar,
  Toolbar,
  IconButton,
  Chip,
  useMediaQuery,
  Menu,
  MenuItem,
  Tooltip,
  Alert,
} from "@mui/material";
import {
  Add,
  Refresh,
  SelectAll,
  Sort,
  ViewList,
  ViewModule,
  Warning,
  MoreVert,
  ChevronRight,
} from "@mui/icons-material";
import useSort from "../../../utils/useSort";
import base64ToImg from "../../../utils/base64ToImg";
import SwalMedia from "../../components/swalMedia";
import biosenseAPI from "../../../api/biosense";
import { useTranslation } from "react-i18next";
import cardOrListTheme from "../../../assets/cardOrListTheme";
import AddProjectComponent from "../mainComponents/projects/addProject";

const phoneTheme = cardOrListTheme;
const theme = cardOrListTheme;

export default function ProjectsScreen({ state, setState, setProjectState, topBarState }) {
  const [projects, setProjects] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [sortedProjects, handleSort, SortFieldIcon] = useSort(projects, "alias");
  const alertProjects = topBarState.alerts.map((alert) => alert.project_id);
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const { t } = useTranslation();

  // 使用 useCallback 缓存函数，避免重复创建
  const handleTabChange = useCallback((e, newValue) => {
    setTabValue(newValue);
  }, []);

  const handleButtonOnClick = useCallback(
    (selectedProject) => {
      setProjectState({
        selectedProject: selectedProject,
      });
      setState({
        pageIdx: state.pageIdx + 1,
      });
    },
    [state, setState, setProjectState],
  );

  const handleRefresh = useCallback(() => {
    setLastUpdated(new Date());
  }, []);

  const handleAddClick = useCallback(() => {
    setAddProjectOpen(true);
  }, []);

  const handleSelectClick = useCallback(() => {
    console.log("Select button clicked");
  }, []);

  // 使用 useEffect 依赖项数组，避免不必要的重新渲染
  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await biosenseAPI.getProjectList();
        setProjects(res.projects);
      } catch (err) {
        setError("Failed to load projects");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [lastUpdated]);

  // 响应式断点
  const isXSmall = useMediaQuery(theme.breakpoints.down("xs"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));

  // 溢出菜单状态
  const [anchorEl, setAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const openOverflow = Boolean(anchorEl);
  const openSort = Boolean(sortAnchorEl);

  // 排序选项
  const sortOptions = [{ field: "alias", text: t("name") }];

  // 处理排序菜单打开
  const handleSortClick = useCallback((event) => {
    setSortAnchorEl(event.currentTarget);
  }, []);

  // 处理排序选择
  const handleSortSelect = useCallback(
    (field) => {
      handleSort(field);
      setSortAnchorEl(null);
    },
    [handleSort],
  );

  // 处理溢出菜单打开
  const handleOverflowClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  // 处理溢出菜单关闭
  const handleOverflowClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // 渲染排序菜单
  const renderSortMenu = () => (
    <Menu
      anchorEl={sortAnchorEl}
      open={openSort}
      onClose={() => setSortAnchorEl(null)}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      {sortOptions.map((item, idx) => (
        <MenuItem key={idx} onClick={() => handleSortSelect(item.field)} sx={{ minWidth: 180 }}>
          <IconButton
            style={{
              flexDirection: "row",
              alignItems: "center",
              borderRadius: "4px",
              padding: "8px",
              gap: 1,
              mr: 2,
            }}
            size='small'
          >
            <SortFieldIcon field={item.field} />
          </IconButton>
          <Typography variant='body1'>{item.text}</Typography>
        </MenuItem>
      ))}
    </Menu>
  );

  // 渲染溢出菜单
  const renderOverflowMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={openOverflow}
      onClose={handleOverflowClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <MenuItem onClick={handleRefresh}>
        <Refresh fontSize='small' sx={{ mr: 2 }} />
        <Typography variant='body1'>{t("refresh")}</Typography>
      </MenuItem>
      <MenuItem onClick={handleAddClick}>
        <Add fontSize='small' sx={{ mr: 2 }} />
        <Typography variant='body1'>{t("add")}</Typography>
      </MenuItem>
      <MenuItem onClick={handleSelectClick}>
        <SelectAll fontSize='small' sx={{ mr: 2 }} />
        <Typography variant='body1'>{t("select")}</Typography>
      </MenuItem>
    </Menu>
  );

  // 工具栏组件
  const ProjectsToolBar = () => {
    return (
      <AppBar position='static' sx={{ backgroundColor: "white", boxShadow: 4 }}>
        <Toolbar
          sx={{
            columnGap: 1,
            flexWrap: "wrap",
            paddingLeft: isXSmall ? 1 : 2,
            paddingRight: isXSmall ? 1 : 2,
          }}
        >
          {/* 视图切换标签 */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label='data display tabs'
            variant={isXSmall ? "fullWidth" : "standard"}
            sx={{
              "& .MuiTabs-flexContainer": {
                flexWrap: "nowrap",
              },
              mr: isMedium ? 1 : 2,
            }}
          >
            <Tab
              icon={<ViewModule />}
              label={isXSmall ? null : t("list.card")}
              aria-label='Cards'
              sx={{
                flexDirection: isXSmall ? "column" : "row",
                alignItems: "center",
                gap: 1,
                minWidth: isXSmall ? "auto" : undefined,
              }}
            />
            <Tab
              icon={<ViewList />}
              label={isXSmall ? null : t("list.list")}
              aria-label='List'
              sx={{
                flexDirection: isXSmall ? "column" : "row",
                alignItems: "center",
                gap: 1,
                minWidth: isXSmall ? "auto" : undefined,
              }}
            />
          </Tabs>

          {/* 排序部分 */}
          {!isSmall && (
            <Box display='flex' alignItems='center' mr={isMedium ? 1 : 2}>
              <Typography variant='body1' color='grey' mr={1}>
                {t("list.sort")}:
              </Typography>

              {isMedium ? (
                <IconButton
                  onClick={handleSortClick}
                  size='small'
                  aria-controls='sort-menu'
                  aria-haspopup='true'
                >
                  <Sort />
                  <Typography variant='body1' ml={1}>
                    {sortOptions.find((opt) => opt.field === handleSort.currentField)?.text ||
                      t("name")}
                  </Typography>
                  <ChevronRight fontSize='small' />
                </IconButton>
              ) : (
                <>
                  {sortOptions.map((item, idx) => (
                    <IconButton
                      key={idx}
                      onClick={() => handleSort(item.field)}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        borderRadius: "4px",
                        padding: "8px",
                        gap: 1,
                      }}
                      size='small'
                    >
                      <SortFieldIcon field={item.field} />
                      <Typography variant='body1'>{item.text}</Typography>
                    </IconButton>
                  ))}
                </>
              )}
            </Box>
          )}

          {/* 右侧操作按钮 */}
          <Box sx={{ flexGrow: 1 }} />

          {!isMedium && (
            <>
              <Typography variant='body' color='grey' mr={2}>
                {t("list.lastupdatedat")}: {lastUpdated.toLocaleString("zh-CN")}
              </Typography>

              <Tooltip title={t("refresh")}>
                <IconButton aria-label='refresh' onClick={handleRefresh} size='small'>
                  <Refresh />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("add")}>
                <IconButton aria-label='add' onClick={handleAddClick} size='small'>
                  <Add />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("select")}>
                <IconButton aria-label='select' edge='end' onClick={handleSelectClick} size='small'>
                  <SelectAll />
                </IconButton>
              </Tooltip>
            </>
          )}

          {/* 溢出菜单 - 小屏幕时显示 */}
          {isMedium && (
            <>
              <Typography
                variant='body'
                color='grey'
                mr={2}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {t("list.lastupdatedat")}: {lastUpdated.toLocaleString("zh-CN")}
              </Typography>

              <IconButton
                aria-label='more'
                aria-controls='overflow-menu'
                aria-haspopup='true'
                onClick={handleOverflowClick}
                size='small'
              >
                <MoreVert />
              </IconButton>
            </>
          )}
        </Toolbar>

        {/* 第二行 - 仅在超小屏幕显示 */}
        {isXSmall && (
          <Toolbar
            variant='dense'
            sx={{
              paddingTop: 0,
              paddingLeft: isXSmall ? 1 : 2,
              paddingRight: isXSmall ? 1 : 2,
            }}
          >
            <Typography variant='body2' color='grey' sx={{ flexGrow: 1 }}>
              {t("list.lastupdatedat")}: {lastUpdated.toLocaleString("zh-CN")}
            </Typography>

            {!isSmall && (
              <IconButton
                aria-label='sort'
                aria-controls='sort-menu'
                aria-haspopup='true'
                onClick={handleSortClick}
                size='small'
              >
                <Sort />
              </IconButton>
            )}
          </Toolbar>
        )}

        {renderSortMenu()}
        {renderOverflowMenu()}
      </AppBar>
    );
  };

  // 加载状态显示
  if (loading) {
    return (
      <Box display={"flex"} flexDirection={"column"} rowGap={3} height={"100%"}>
        <ProjectsToolBar />
        <Box flexGrow={1} display='flex' justifyContent='center' alignItems='center'>
          <CircularProgress sx={{ mt: 1 }} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display={"flex"} flexDirection={"column"} rowGap={3} height={"100%"}>
        <ProjectsToolBar />
        <Box flexGrow={1} display='flex' justifyContent='center' alignItems='center'>
          <Alert severity='error'>{error}</Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box display={"flex"} flexDirection={"column"} rowGap={3}>
      <ProjectsToolBar />
      {tabValue ? (
        <List
          sx={{
            backgroundColor: "white",
            p: 1.5,
            borderRadius: "4px",
            boxShadow: 4,
          }}
        >
          {sortedProjects.map((project, idx) => (
            <ListItem
              key={`projects-list-${idx}`}
              project={project}
              buttonOnClick={() => handleButtonOnClick(project)}
              t={t}
              alertProjects={alertProjects}
            />
          ))}
        </List>
      ) : (
        <ThemeProvider theme={theme}>
          <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12, lg: 12, xl: 12 }}>
            {sortedProjects.map((project, idx) => (
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={4}
                xl={3}
                key={project.id}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                }}
              >
                <Card
                  key={`projects-card-${idx}`}
                  project={project}
                  buttonOnClick={() => handleButtonOnClick(project)}
                  t={t}
                  alertProjects={alertProjects}
                />
              </Grid>
            ))}
          </Grid>
        </ThemeProvider>
      )}
      {addProjectOpen && (
        <AddProjectComponent
          open={addProjectOpen}
          onClose={() => setAddProjectOpen(false)}
          onProjectAdded={() => setLastUpdated(new Date())}
        />
      )}
    </Box>
  );
}

const ListItem = ({ project, buttonOnClick, t, alertProjects }) => {
  const projectId = project.id;
  const isAlertProject = alertProjects.includes(projectId);
  return (
    <ProtoListItem
      sx={{
        borderLeft: isAlertProject ? "6px solid #FF9966" : "none",
        padding: 2,
        "&:last-child": {
          borderBottom: 0,
        },
      }}
    >
      <ListItemAvatar>
        <SwalMedia
          component='Avatar'
          title={project.alias ?? "(沒有名字)"}
          height='60'
          width='60'
          style={{
            objectFit: "cover",
          }}
          src={base64ToImg({ base64String: project.img })}
          alt='List Avatar'
        />
      </ListItemAvatar>
      <Box display='flex' flexDirection='column' flexGrow={1}>
        <ListItemText
          sx={{
            flexGrow: 1,
            "& .MuiListItemText-secondary": {
              whiteSpace: "pre-wrap",
              fontSize: 13,
            },
          }}
          primary={
            <Box display='flex' alignItems='center'>
              <Typography variant='body1' fontWeight={500}>
                {project.alias ?? "(沒有名字)"}
              </Typography>
              {isAlertProject ? (
                <Chip
                  sx={{
                    ml: 1,
                    p: 0.25,
                    backgroundColor: "#ff996688",
                    color: "black",
                    zIndex: 1,
                    fontSize: 11,
                  }}
                  label={t("project.pendingalert")}
                  icon={<Warning color='black' fontSize='small' />}
                />
              ) : null}
            </Box>
          }
          secondary={"item description"}
        />
      </Box>
      <Button
        variant='contained'
        color='warning'
        sx={{ borderRadius: "4px", textTransform: "none" }}
        onClick={buttonOnClick}
      >
        {t("list.details")}
      </Button>
    </ProtoListItem>
  );
};

const Card = ({ project, buttonOnClick, t, alertProjects }) => {
  const projectId = project.id;
  const isAlertProject = alertProjects.includes(projectId);
  return (
    <Box
      display='flex'
      position='relative'
      flexDirection='column'
      width='100%'
      sx={{
        backgroundColor: "white",
        p: 1.5,
        borderRadius: "4px",
        boxShadow: 4,
        borderBottom: isAlertProject ? "6px solid #FF9966" : "none",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: 6,
          transform: "translateY(-2px)",
        },
      }}
    >
      {isAlertProject ? (
        <Chip
          sx={{
            p: 1,
            position: "absolute",
            right: "16px",
            top: "16px",
            backgroundColor: "#7D7D7D99",
            color: "white",
            zIndex: 1,
          }}
          label={t("project.pendingalert")}
          icon={<Warning color='white' />}
        />
      ) : null}
      <SwalMedia
        component='img'
        title={project.alias ?? "(沒有名字)"}
        height='180'
        width='100%'
        style={{
          objectFit: "cover",
          borderTopLeftRadius: "4px",
          borderTopRightRadius: "4px",
        }}
        src={base64ToImg({ base64String: project.img })}
        alt='Card Background'
      />
      <Box
        display='flex'
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          backgroundColor: "#39729B",
          opacity: 0.9,
          color: "white",
          padding: "5px 5px 5px 10px",
          borderBottomLeftRadius: "4px",
          borderBottomRightRadius: "4px",
        }}
      >
        <Typography>{project.alias ?? "(沒有名字)"}</Typography>
        <Button
          variant='contained'
          color='warning'
          sx={{ borderRadius: "4px", textTransform: "none" }}
          onClick={buttonOnClick}
        >
          <Typography color='white'>{t("list.details")}</Typography>
        </Button>
      </Box>
    </Box>
  );
};
